<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { frontendURL } from 'dashboard/helper/URLHelper';

import SettingsLayout from '../SettingsLayout.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import BotBrainTab from './components/BotBrainTab.vue';
import BotChannelsTab from './components/BotChannelsTab.vue';
import BotBehaviorTab from './components/BotBehaviorTab.vue';

const TABS = [
  { key: 'brain', labelKey: 'AGENT_BOTS.TABS.BRAIN' },
  { key: 'channels', labelKey: 'AGENT_BOTS.TABS.CHANNELS' },
  { key: 'behavior', labelKey: 'AGENT_BOTS.TABS.BEHAVIOR' },
];

const BRAIN_LABEL_KEYS = {
  ai_agent: 'AGENT_BOTS.TYPES.AI_AGENT',
  flow: 'AGENT_BOTS.TYPES.FLOW',
  webhook: 'AGENT_BOTS.TYPES.WEBHOOK',
  remote: 'AGENT_BOTS.TYPES.REMOTE',
};

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const agentBots = useMapGetter('agentBots/getBots');
const uiFlags = useMapGetter('agentBots/getUIFlags');

const activeTab = ref('brain');

const bot = computed(
  () =>
    agentBots.value.find(item => item.id === Number(route.params.botId)) || {}
);

const brainLabel = computed(() =>
  t(BRAIN_LABEL_KEYS[bot.value.bot_type] || BRAIN_LABEL_KEYS.webhook)
);

const channelLabel = computed(() =>
  bot.value.inboxes?.length
    ? bot.value.inboxes.map(inbox => inbox.name).join(', ')
    : t('AGENT_BOTS.NO_CHANNEL')
);

const tabs = computed(() =>
  TABS.map(tab => ({ ...tab, label: t(tab.labelKey) }))
);

const updateBot = async data => {
  try {
    await store.dispatch('agentBots/update', { id: bot.value.id, data });
    useAlert(t('AGENT_BOTS.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_BOTS.EDIT.API.ERROR_MESSAGE'));
  }
};

const toggleActive = () => updateBot({ active: !bot.value.active });

const goBack = () => {
  router.push(
    frontendURL(`accounts/${route.params.accountId}/settings/agent-bots`)
  );
};

// An AI agent's bot page is the agent's own, with its instructions, knowledge and inboxes.
watch(
  bot,
  value => {
    if (value.bot_type !== 'ai_agent' || !value.bot_config?.assistant_id)
      return;
    router.replace({
      name: 'captain_assistants_settings_index',
      params: {
        accountId: route.params.accountId,
        assistantId: value.bot_config.assistant_id,
      },
    });
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('agentBots/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching && !bot.id"
    :loading-message="t('AGENT_BOTS.LIST.LOADING')"
  >
    <template #header>
      <div class="flex flex-col gap-4">
        <button
          type="button"
          class="flex items-center gap-1 text-sm w-fit text-n-slate-11 hover:text-n-slate-12"
          @click="goBack"
        >
          <span class="i-lucide-chevron-left size-4" />
          {{ $t('AGENT_BOTS.HEADER') }}
        </button>

        <div class="flex items-center gap-4">
          <Avatar
            :name="bot.name || ''"
            :src="bot.thumbnail"
            :size="40"
            class="shrink-0"
          />
          <div class="flex flex-col flex-grow min-w-0 gap-1">
            <div class="flex items-center gap-2">
              <h1 class="text-xl font-medium text-n-slate-12">
                {{ bot.name }}
              </h1>
              <span
                class="px-2 py-0.5 text-xs rounded-md text-n-slate-12 bg-n-alpha-2"
              >
                {{ brainLabel }}
              </span>
            </div>
            <span class="text-sm text-n-slate-11">
              {{
                bot.inboxes?.length
                  ? $t('AGENT_BOTS.SHOW.ANSWERS_IN', { channels: channelLabel })
                  : $t('AGENT_BOTS.SHOW.ANSWERS_NOWHERE')
              }}
            </span>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <span class="text-sm text-n-slate-11">
              {{
                bot.active
                  ? $t('AGENT_BOTS.STATUS.ACTIVE')
                  : $t('AGENT_BOTS.STATUS.INACTIVE')
              }}
            </span>
            <Switch
              :model-value="bot.active"
              :is-loading="uiFlags.isUpdating"
              @change="toggleActive"
            />
          </div>
        </div>

        <div class="flex items-center gap-6 border-b border-n-weak">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            class="pb-2 -mb-px text-sm border-b-2"
            :class="
              activeTab === tab.key
                ? 'border-n-brand text-n-slate-12'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
            "
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>
    </template>

    <template #body>
      <div v-if="bot.id">
        <BotBrainTab v-if="activeTab === 'brain'" :bot="bot" />
        <BotChannelsTab v-else-if="activeTab === 'channels'" :bot="bot" />
        <BotBehaviorTab
          v-else
          :bot="bot"
          :is-updating="uiFlags.isUpdating"
          @save="updateBot"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
